import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Modal from '../../../components/shared/Modal';
import { useQuery } from '@tanstack/react-query';
import api from '../../../services/api';

const schema = yup.object().shape({
  nome: yup.string().required('O nome é obrigatório'),
  slug: yup.string().required('O slug é obrigatório').matches(/^[a-z0-9-]+$/, 'O slug deve conter apenas letras minúsculas, números e hífens'),
  duracao: yup.number().typeError('A duração deve ser um número').required('A duração é obrigatória').min(1, 'A duração deve ser de no mínimo 1 minuto'),
  descricao: yup.string(),
  empreendimento_id: yup.string().nullable(),
  localizacao: yup.string(),
  instrucoes: yup.string(),
  cal_link: yup.string().url('Insira uma URL válida'),
  ativo: yup.boolean(),
});

const generateSlug = (name) => {
  return name
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // remove accents
    .replace(/[^a-z0-9\s-]/g, '') // remove special chars
    .trim()
    .replace(/\s+/g, '-') // replace spaces with hyphens
    .replace(/-+/g, '-'); // remove consecutive hyphens
};

const fetchEmpreendimentos = async () => (await api.get('/empreendimentos')).data.data;

export default function TipoDeEventoModal({ isOpen, onClose, onSave, evento }) {
  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      nome: '',
      slug: '',
      duracao: 30,
      descricao: '',
      empreendimento_id: null,
      localizacao: '',
      instrucoes: '',
      cal_link: '',
      ativo: true,
    }
  });

  const { data: empreendimentos } = useQuery({
    queryKey: ['empreendimentosList'],
    queryFn: fetchEmpreendimentos,
    enabled: isOpen
  });

  const nomeValue = watch('nome');

  useEffect(() => {
    if (nomeValue && !evento) { // Only auto-generate for new events
      setValue('slug', generateSlug(nomeValue));
    }
  }, [nomeValue, evento, setValue]);

  useEffect(() => {
    if (isOpen) {
      if (evento) {
        reset(evento);
      } else {
        reset({
          nome: '',
          slug: '',
          duracao: 30,
          descricao: '',
          empreendimento_id: null,
          localizacao: '',
          instrucoes: '',
          cal_link: '',
          ativo: true,
        });
      }
    }
  }, [isOpen, evento, reset]);

  const onSubmit = (data) => {
    onSave(data);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={evento ? 'Editar Tipo de Evento' : 'Novo Tipo de Evento'} size="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Nome do Evento (Ex: Visita ao Decorado)</label>
            <input {...register('nome')} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm" />
            <p className="text-red-500 text-xs mt-1">{errors.nome?.message}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Slug (Link amigável)</label>
            <input {...register('slug')} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm" />
            <p className="text-red-500 text-xs mt-1">{errors.slug?.message}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Duração (minutos)</label>
            <input type="number" {...register('duracao')} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm" />
            <p className="text-red-500 text-xs mt-1">{errors.duracao?.message}</p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Link do Cal.com (URL Completa)</label>
            <input {...register('cal_link')} placeholder="https://cal.com/seu-link" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm" />
            <p className="text-red-500 text-xs mt-1">{errors.cal_link?.message}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Vincular ao Empreendimento</label>
            <select {...register('empreendimento_id')} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm">
              <option value="">Nenhum</option>
              {empreendimentos?.map(e => (
                <option key={e.id} value={e.id}>{e.nome}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <div className="mt-2 flex items-center">
              <input type="checkbox" {...register('ativo')} className="h-4 w-4 text-primary border-gray-300 rounded" />
              <label className="ml-2 block text-sm text-gray-900">Ativo</label>
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Localização (Endereço do Plantão)</label>
            <input {...register('localizacao')} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm" />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Instruções de Agendamento (Mensagem de Sucesso)</label>
            <textarea {...register('instrucoes')} rows="3" placeholder="Ex: Procurar por Paulo na recepção..." className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"></textarea>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Descrição Interna (Opcional)</label>
            <textarea {...register('descricao')} rows="2" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"></textarea>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
            Cancelar
          </button>
          <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-secondary">
            Salvar
          </button>
        </div>
      </form>
    </Modal>
  );
}

